import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Check, ClipboardCheck, Loader2, Undo2 } from 'lucide-react';
import { Button } from '@/Components/ui/button';
import { Input } from '@/Components/ui/input';
import { Label } from '@/Components/ui/label';
import { Switch } from '@/Components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/Components/ui/dialog';
import { useCompanyAccess } from '@/HireWithUs/Context/CompanyAccessContext';
import {
  approvalError, decideApproval, getOfferDraftPdf, getOfferPolicy, getWaitingApprovals, setOfferPolicy,
} from '../Interview/offerApprovalsApi';

const POLL_MS = 20000;
const NO_POLICY = { needsApproval: false, available: false };

// Refusals that mean the request is no longer waiting, so it leaves the list.
const GONE = ['already_decided', 'offer_changed', 'not_found'];

/**
 * The company's approval switch and the offers waiting for a decision. Nothing is
 * fetched for people who can't use either, and a server without approvals answers
 * with an error, which leaves the page as it always was.
 */
export function useOfferApprovals() {
  const { can, ready } = useCompanyAccess();
  const seesPolicy = can(['offers.view', 'offers.draft', 'offers.approve']);
  const approves = can('offers.approve');
  const [policy, setPolicy] = useState(NO_POLICY);
  const [waiting, setWaiting] = useState([]);

  const reload = useCallback(async () => {
    if (!ready || !seesPolicy) return;
    try {
      const next = await getOfferPolicy();
      setPolicy(next);
      setWaiting(approves && next.needsApproval ? await getWaitingApprovals() : []);
    } catch (err) {
      setWaiting([]);
    }
  }, [ready, seesPolicy, approves]);

  useEffect(() => {
    reload();
    const interval = window.setInterval(() => {
      if (!document.hidden) reload();
    }, POLL_MS);
    return () => window.clearInterval(interval);
  }, [reload]);

  const setNeedsApproval = useCallback(async (needsApproval) => {
    const saved = await setOfferPolicy(needsApproval);
    setPolicy((current) => ({ ...current, needsApproval: saved }));
    await reload();
    return saved;
  }, [reload]);

  return { policy, waiting, reload, setNeedsApproval };
}

/** "Offers need approval", for people who run the company's settings. */
export function OfferApprovalSwitch({ policy, onChange }) {
  const { can } = useCompanyAccess();
  const [saving, setSaving] = useState(false);
  if (!policy.available || !can('settings.company')) return null;

  const change = async (next) => {
    setSaving(true);
    try {
      const saved = await onChange(next);
      toast.success(saved
        ? 'Offers now need approval before they are sent.'
        : 'Offers no longer need approval.');
    } catch (err) {
      toast.error(approvalError(err, 'The setting could not be saved. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="inline-flex h-9 items-center gap-2 rounded-md border border-slate-200 bg-white px-3"
      title="When this is on, an offer is sent only after someone who approves offers has approved it."
    >
      <Switch
        id="offers-need-approval"
        checked={policy.needsApproval}
        disabled={saving}
        onCheckedChange={change}
      />
      <Label htmlFor="offers-need-approval" className="cursor-pointer text-sm font-medium text-slate-700">
        Offers need approval
      </Label>
    </div>
  );
}

function ApprovalReview({ item, displayName, formatDate, onClose, onDecided }) {
  const [pdfUrl, setPdfUrl] = useState('');
  const [loadFailed, setLoadFailed] = useState(false);
  const [sendingBack, setSendingBack] = useState(false);
  const [reason, setReason] = useState('');
  const [working, setWorking] = useState('');

  useEffect(() => {
    setPdfUrl('');
    setLoadFailed(false);
    setSendingBack(false);
    setReason('');
    if (!item) return undefined;
    let live = true;
    let url = '';
    getOfferDraftPdf(item.application_id)
      .then((blob) => {
        if (!live) return;
        url = URL.createObjectURL(blob);
        setPdfUrl(url);
      })
      .catch(() => {
        if (live) setLoadFailed(true);
      });
    return () => {
      live = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [item]);

  const decide = async (approve) => {
    setWorking(approve ? 'approve' : 'reject');
    try {
      await decideApproval(item.approval_id, approve, approve ? '' : reason);
      toast.success(approve ? 'Offer approved. It can now be sent.' : 'Offer sent back.');
      onDecided();
      onClose();
    } catch (err) {
      const code = err?.response?.data?.code;
      toast.error(code === 'offer_changed'
        ? 'This offer changed after approval was asked for, so it needs a new request.'
        : approvalError(err, 'That did not work. Please try again.'));
      if (GONE.includes(code)) {
        onDecided();
        onClose();
      }
    } finally {
      setWorking('');
    }
  };

  const name = item ? displayName(item.candidate_name) || 'the candidate' : '';
  return (
    <Dialog open={Boolean(item)} onOpenChange={(open) => { if (!open && !working) onClose(); }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Offer for {name}</DialogTitle>
          <DialogDescription>
            {item?.job_title || 'Job'} · Asked by {item?.requested_by} on {formatDate(item?.requested_at)}
          </DialogDescription>
        </DialogHeader>
        {item?.note && <p className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700">{item.note}</p>}
        {pdfUrl && <iframe title="Offer letter" src={pdfUrl} className="h-[60vh] w-full rounded border bg-white" />}
        {!pdfUrl && !loadFailed && (
          <div className="flex h-40 items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading the offer letter…
          </div>
        )}
        {loadFailed && (
          <p className="rounded-md border px-3 py-6 text-center text-sm text-muted-foreground">
            The offer letter could not be loaded, so it can't be approved from here. You can still send it back.
          </p>
        )}
        <DialogFooter className="gap-2 sm:gap-2">
          {!item?.can_decide && (
            <>
              <span className="mr-auto self-center text-xs text-muted-foreground">
                Someone else has to approve an offer you asked approval for.
              </span>
              <Button type="button" variant="outline" onClick={onClose}>Close</Button>
            </>
          )}
          {item?.can_decide && sendingBack && (
            <>
              <Input
                aria-label="What needs to change"
                placeholder="What needs to change?"
                className="h-9 sm:mr-auto sm:w-72"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
              <Button type="button" variant="outline" onClick={() => setSendingBack(false)} disabled={Boolean(working)}>
                Back
              </Button>
              <Button type="button" onClick={() => decide(false)} disabled={!reason.trim() || Boolean(working)}>
                {working === 'reject' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Undo2 className="mr-2 h-4 w-4" />}
                Send back
              </Button>
            </>
          )}
          {item?.can_decide && !sendingBack && (
            <>
              <Button type="button" variant="outline" onClick={onClose} disabled={Boolean(working)}>Close</Button>
              <Button type="button" variant="outline" onClick={() => setSendingBack(true)} disabled={Boolean(working)}>
                <Undo2 className="mr-2 h-4 w-4" />
                Send back
              </Button>
              <Button type="button" onClick={() => decide(true)} disabled={!pdfUrl || Boolean(working)}>
                {working === 'approve' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
                Approve
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Offers waiting for a decision, oldest first. An approver opens the letter before
 * deciding, so they approve what the candidate will receive.
 */
export function WaitingApprovals({ items, onDecided, displayName, formatDate }) {
  const [reviewing, setReviewing] = useState(null);
  if (!items.length && !reviewing) return null;
  return (
    <>
      {items.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-amber-200 bg-white">
          <div className="flex items-center gap-2 bg-amber-50 px-4 py-2.5">
            <ClipboardCheck className="h-4 w-4 text-amber-600" />
            <span className="text-sm font-semibold text-slate-800">Waiting for approval</span>
            <span className="ml-1 rounded-full bg-white px-2 text-xs font-semibold text-amber-600">{items.length}</span>
          </div>
          <div className="divide-y divide-slate-100">
            {items.map((item) => (
              <div key={item.approval_id} className="flex flex-wrap items-center gap-3 px-4 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-foreground">
                    {displayName(item.candidate_name) || 'Candidate'}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    {item.job_title || '-'} · Asked by {item.requested_by} · {formatDate(item.requested_at)}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant={item.can_decide ? 'default' : 'outline'}
                  onClick={() => setReviewing(item)}
                >
                  {item.can_decide ? 'Review' : 'View'}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
      <ApprovalReview
        item={reviewing}
        displayName={displayName}
        formatDate={formatDate}
        onClose={() => setReviewing(null)}
        onDecided={onDecided}
      />
    </>
  );
}
